import { IQueryParams, RequestStrategyEnum } from '@shared/api/models';
import { createEvent } from 'effector';

const API_DELAY = 300;

//**In case of abort strategy usage also used abortApiFailCaseTrigger if fail case of effect to prevent error events or loader blinks*/
//**In case of abort strategy usage to prevent error messages from store also use errorStoreAbortRequestWatcher in $errorStore.watch */

export class ApiUtils {
  static wrapperApi = <T>(effect: any, strategy: RequestStrategyEnum = RequestStrategyEnum.Default) => {
    const updateEvent = createEvent<T>();
    let timeoutId: NodeJS.Timeout | null = null;
    const uniqueRequests = new Set<string>();
    const uniqueRequestsList: any[] = [];
    let abortController: AbortController | null = null;

    const debounceHandler = (request: any) => {
      const requestKey = JSON.stringify(request);

      if (!uniqueRequests?.has(requestKey)) {
        uniqueRequests.add(requestKey);
        uniqueRequestsList.push(request);

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          uniqueRequestsList?.forEach((req) => {
            const result = effect(req);

            if (result && typeof result.finally === 'function') {
              result.finally(() => {
                uniqueRequests.delete(JSON.stringify(req));
              });
            } else {
              uniqueRequests.delete(JSON.stringify(req));
            }
          });
          uniqueRequestsList.length = 0;
        }, API_DELAY);
      }
    };

    const withTakeLatest = (effect: any) => {
      const wrappedEffect = effect.prepend((request: any) => {
        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();

        return {
          ...request,
          signal: abortController.signal,
        };
      });

      return wrappedEffect;
    };

    updateEvent.watch((request) => {
      const requestKey = JSON.stringify(request);

      const requestObject = { request };

      if (strategy === RequestStrategyEnum.Default) {
        if (!uniqueRequests.has(requestKey)) {
          uniqueRequests.add(requestKey);
          const result = effect(requestObject);
          if (result && typeof result.finally === 'function') {
            result.finally(() => {
              uniqueRequests.delete(requestKey);
            });
          } else {
            uniqueRequests.delete(requestKey);
          }
        }
      } else if (strategy === RequestStrategyEnum.Debounce) {
        debounceHandler(requestObject);
      } else if (strategy === RequestStrategyEnum.Abort) {
        const wrappedEffect = withTakeLatest(effect);
        const result = wrappedEffect(requestObject);
        if (result && typeof result.finally === 'function') {
          result.finally(() => {
            uniqueRequests.delete(requestKey);
          });
        } else {
          uniqueRequests.delete(requestKey);
        }
      }
    });

    return updateEvent;
  };

  static abortApiFailCaseTrigger = <T>(data: { params?: void | IQueryParams<T> | undefined; error: Error }) => data.error.name === 'AbortError';

  static errorStoreAbortRequestWatcher = (message: string | null, event: (message: string | null) => void) => {
    if (message === 'Cannot delete all account') {
      event('Cannot delete all accounts');
      return;
    }
    if (message === `Cannot delete all card`) {
      event(`Cannot delete all cards`);
      return;
    }

    if (message !== 'signal is aborted without reason') event(message);
  };
  static errorStoreActionForbiddenWatcher = (message: string | null, event: (message: string | null) => void) => {
    const accountMessage = `'Account Id' must not be empty. 'Account Id' must be greater than '0'.`;
    const cardMessage = `'Card Id' must be greater than '0'.`;
    const userManagementMessage = 'User Id cannot be empty.';
    const userManagementMessageTwo = `Could not retrieve user.`;
    const recipientMessage = `'Id' must not be empty.`;
    const cardDeleted = `Cannot delete all card`;
    const accountDeleted = 'Cannot delete all account';

    if (!message) {
      event(null);
      return;
    }

    if (message === cardDeleted) {
      event('Cannot delete all cards');
      return;
    }
    if (message === accountDeleted) {
      event('Cannot delete all accounts');
      return;
    }

    if (
      message === accountMessage ||
      message === cardMessage ||
      message === userManagementMessage ||
      message === userManagementMessageTwo ||
      message === recipientMessage
    ) {
      event('Action is forbidden.');
      return;
    }
    event(message);
  };
}
